import { useState, useCallback } from "react";
import { generateGrantReport } from "../../services/relatorioOutorga";
import { grantStatus } from "../../utils/Enums/grantStatus";
import { getStatusColor } from "../../utils/outorga/dashboard";
import helper from '../../utils/helper'
import { usePdfExport } from "../usePdfExport";

const buildReport = (report) => {
    if (!report?.outorgas) return null;

    return {
        period: `Período: ${report?.periodo?.inicio.split(" ")[0]} - ${report?.periodo?.fim.split(" ")[0]}`,
        grants: report.outorgas.map((grant) => ({
            name: grant?.nome,
            info: {
                monitored: grant?.monitorado_nome,
                captation: helper.toCapitalizeCase(grant?.captacao),
                refId: grant?.id_referencia,
                validity: grant?.validade?.split(" ")[0]
            },
            fields: (grant.condicoes_ativas || []).map((c) => ({
                name: c?.nome,
                alert: `${c?.condicao?.alerta?.inicio} até ${c?.condicao?.alerta?.fim}`,
                critical: `${c?.condicao?.critico?.inicio} até ${c?.condicao?.critico?.fim}`,
                exceeded: `${c?.condicao?.excedido?.inicio} até ${c?.condicao?.excedido?.fim}`
            })),
            months: (grant?.meses || []).map((mes) => ({
                name: mes?.label,
                totalMonthViolations: mes?.total_violacoes_mes ?? 0,
                days: (mes?.dias || []).map((dia) => {
                    if (dia?.sem_violacoes || !dia?.condicoes?.length) {
                        return {
                            date: dia?.data,
                            noOccurrences: true,
                            status: dia?.status,
                            status_name: grantStatus[dia?.status]
                        };
                    }

                    return {
                        date: dia?.data,
                        totalViolations: dia?.total_violacoes_dia ?? 0,
                        status_name: grantStatus[dia?.status],
                        violations: (dia?.condicoes || []).map((cond) => ({
                            name: cond?.nome,
                            value: cond?.valor_dia,
                            above: cond?.margem_excedido ?? ' —',
                            status: cond?.status
                        }))
                    };
                })
            })),
            grantStatus: grant?.status
        }))
    };
};

const buildCsv = (data) => {
    if (!data) return { columns: [], data: [] };

    const columns = [
        { id: "c1", displayName: "" },
        { id: "c2", displayName: "" },
        { id: "c3", displayName: "" },
        { id: "c4", displayName: "" },
        { id: "c5", displayName: "" },
        { id: "c6", displayName: "" },
    ];

    const rows = [];

    data?.grants.forEach((grant) => {
        rows.push({
            c1: "Descrição",
            c2: "Monitorado",
            c3: "Captação",
            c4: "ID Ref",
            c5: "Validade",
            c6: "Status"
        });

        rows.push({
            c1: grant?.name,
            c2: grant?.info?.monitored,
            c3: grant?.info?.captation,
            c4: grant?.info?.refId,
            c5: grant?.info?.validity,
            c6: grantStatus[grant?.grantStatus]
        });

        rows.push({});

        rows.push({
            c1: "Parâmetro",
            c2: "Alerta",
            c3: "Crítico",
            c4: "Excedido"
        });

        grant?.fields.forEach((c) => {
            rows.push({
                c1: c?.name,
                c2: c?.alert,
                c3: c?.critical,
                c4: c?.exceeded
            });
        });

        rows.push({});

        grant?.months.forEach((month) => {
            rows.push({
                c1: `${month?.name} - ${month?.totalMonthViolations} ${month?.totalMonthViolations == 1 ? "Dia com violação" : "Dias com violações"}`
            });

            month?.days?.forEach((day) => {
                if (day?.noOccurrences) {
                    rows.push({ c1: `${day?.date} - ${day?.status_name}` });
                } else {
                    rows.push({
                        c1: `${day?.date} - ${day?.totalViolations} ${day?.totalViolations === 1 ? "Parâmetro com violação" : "Parâmetros com violações"}`
                    });

                    rows.push({
                        c1: "Parâmetro",
                        c2: "Valor",
                        c3: "Acima",
                        c4: "Status"
                    });

                    day?.violations.forEach((v) => {
                        rows.push({
                            c1: v?.name,
                            c2: v?.value,
                            c3: v?.above,
                            c4: grantStatus[v?.status]
                        });
                    });
                }
            });

            rows.push({});
        });

        rows.push({});
        rows.push({});
    });

    return { columns, data: rows };
};

const buildPdf = (reportData) => {
    const content = [
        { text: "Relatório de Outorga", style: "header", margin: [0, 0, 0, 10] },
        { text: reportData.period, margin: [0, 0, 0, 10] }
    ];

    reportData?.grants?.forEach((grant, index) => {
        const body = [];

        body.push([
            { text: "Descrição", bold: true, fontSize: 14, fillColor: "#eeeeee" },
            { text: "Monitorado", bold: true, fontSize: 14, fillColor: "#eeeeee" },
            { text: "Captação", bold: true, fontSize: 14, fillColor: "#eeeeee" },
            { text: "ID Ref", bold: true, fontSize: 14, fillColor: "#eeeeee" },
            { text: "Validade", bold: true, fontSize: 14, fillColor: "#eeeeee" },
            { text: "Status", bold: true, fontSize: 14, fillColor: "#eeeeee" }
        ]);

        body.push([
            grant?.name,
            grant?.info?.monitored,
            grant?.info?.captation,
            grant?.info?.refId,
            grant?.info?.validity,
            grantStatus[grant?.grantStatus]
        ]);

        body.push([
            { text: "Parâmetro", bold: true, colSpan: 3 },
            {},
            {},
            { text: "Alerta", bold: true },
            { text: "Crítico", bold: true },
            { text: "Excedido", bold: true },
        ]);

        grant?.fields?.forEach((c) => {
            body.push([
                { text: c?.name, colSpan: 3 },
                {},
                {},
                c?.alert,
                c?.critical,
                c?.exceeded,
            ]);
        });

        grant?.months?.forEach((month) => {
            body.push([
                {
                    text: `${month?.name} - ${month?.totalMonthViolations} ${month?.totalMonthViolations == 1 ? "Dia com violação" : "Dias com violações"}`,
                    colSpan: 6,
                    bold: true
                },
                {},
                {},
                {},
                {},
                {}
            ]);

            month?.days?.forEach((day) => {
                if (day?.noOccurrences) {
                    body.push([
                        {
                            text: `${day?.date} - ${day?.status_name}`,
                            italics: true,
                            colSpan: 6,
                            color: getStatusColor(day?.status)
                        },
                        {},
                        {},
                        {},
                        {},
                        {}
                    ]);
                } else {
                    body.push([
                        {
                            text: `${day?.date} - ${day?.totalViolations} ${day?.totalViolations === 1 ? "Parâmetro com violação" : "Parâmetros com violações"} (${day?.status_name})`,
                            colSpan: 6,
                            italics: true
                        },
                        {},
                        {},
                        {},
                        {},
                        {}
                    ]);

                    body.push([
                        { text: "Parâmetro", bold: true, colSpan: 3 },
                        {},
                        {},
                        { text: "Valor", bold: true },
                        { text: "Acima", bold: true },
                        { text: "Status", bold: true }
                    ]);

                    day?.violations?.forEach((v) => {
                        body.push([
                            { text: v?.name, colSpan: 3, color: getStatusColor(v?.status) },
                            {},
                            {},
                            { text: v?.value, color: getStatusColor(v?.status) },
                            { text: v?.above, color: getStatusColor(v?.status) },
                            { text: grantStatus[v?.status], color: getStatusColor(v?.status) }
                        ]);
                    });
                }
            });
        });

        content.push({
            table: {
                widths: ["*", "*", "*", "*", "*", "*"],
                body
            },
            margin: [0, 0, 0, 15],
            pageBreak: index !== reportData?.grants?.length - 1 ? "after" : undefined
        });
    });

    return content;
};

export function useGrantReport() {
    const { exportPdf } = usePdfExport();
    const [filters, setFilters] = useState({
        period: {},
        grants: [],
        onlyViolations: false
    });

    const [reportData, setReportData] = useState([]);
    const [csv, setCsv] = useState({ columns: [], data: [] });
    const [generated, setGenerated] = useState(false);
    const [loading, setLoading] = useState(false);

    const generateReport = useCallback(async () => {
        const { period, grants, onlyViolations } = filters;

        const payload = {
            periodo: {
                data_inicio: period.startDate,
                data_fim: period.endDate
            },
            outorgas_ids: grants.map((grant) => grant?.value),
            somente_violacoes: onlyViolations
        };

        setLoading(true);

        try {
            const response = await generateGrantReport(payload);

            const parsed = buildReport(response.data);

            setReportData(parsed);
            setCsv(buildCsv(parsed));
            setGenerated(true);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const handleExportPDF = useCallback(() => {
        if (!reportData?.grants?.length) return;

        const content = buildPdf(reportData) || [];

        exportPdf({
            content,
            pageOrientation: "landscape",
            styles: {
                header: { fontSize: 16, bold: true }
            }
        });
    }, [reportData, exportPdf]);

    const canGenerate = useCallback(() => {
        const { period } = filters;

        return Object.keys(period).length;
    }, [filters]);

    return {
        filters,
        setFilters,
        reportData,
        csv,
        generated,
        loading,
        generateReport,
        handleExportPDF,
        canGenerate
    };
}